import { AxiosResponse } from "axios"
import { Observable } from "rxjs"

import {
  BasicTopic,
  DataCategoryEntity,
  ExpertAnswerData,
  ExpertArticleDTO,
  ExpertArticleInsightDTO,
  ExpertSubtopicDTO,
  ExpertTopicDTO,
  KnowledgeDatatypeFilters,
} from "@framework/types/knowledge"
import {
  AnswerStatus,
  ExpertQuestionDTO,
  ExpertQuestionsDataDTO,
} from "@framework/types/question"
import { BaseUserData, RankedExpertData } from "@framework/types/user"
import {
  BrandExpertMapping,
  BrandExpertResolution,
  BrandExpertRole,
  ExpertQuestionType,
  RoutableBrand,
} from "@framework/types/brand-expert"
import { PaginationListMeta, PaginationParams } from "@framework/types/utils"
import { Entity } from "@framework/types/common"

import {
  CollectionContentModeType,
  DefaultSuccessResponse,
} from "./common/types"
import HttpService from "./http.service"

export interface GetExpertsResponse extends DefaultSuccessResponse {
  data: {
    experts: BaseUserData[]
  }
}

export interface GetRankedExpertsResponse extends DefaultSuccessResponse {
  data: RankedExpertData[]
}

export interface GetExpertQuestionsResponse extends DefaultSuccessResponse {
  data: ExpertQuestionsDataDTO
}

export interface GetDataCategoriesByAvatarResponse {
  data: DataCategoryEntity[]
}

export interface GetAvatarAnswersResponse extends DefaultSuccessResponse {
  data: ExpertAnswerData[]
  meta: PaginationListMeta
}

export interface GetExpertTopicResponse {
  data?: ExpertTopicDTO
}

export interface GetExpertTopicListResponse {
  data?: ExpertTopicDTO[]
}

export interface GetExpertSubtopicResponse {
  data: ExpertSubtopicDTO
}

export interface GetExpertSubtopicListResponse {
  data?: ExpertSubtopicDTO[]
}

export interface GetExpertArticleListResponse {
  data?: ExpertArticleDTO[]
}

export interface GetExpertArticleResponse {
  data: ExpertArticleDTO
}

export interface GetExpertArticleInsightResponse {
  data?: ExpertArticleInsightDTO
}

export interface GetExpertArticleInsightListResponse {
  data?: ExpertArticleInsightDTO[]
}

class KnowledgeAPI extends HttpService {
  getKnowledgeExperts = (): Promise<AxiosResponse<GetExpertsResponse>> =>
    this.get(`ts/v2/knowledge/experts?knowledgeType=gap`)

  getRankedExperts = (
    avatarId: string
  ): Promise<AxiosResponse<GetRankedExpertsResponse>> =>
    this.get(`ts/v2/knowledge/avatar/${avatarId}/expert-ranking`)

  getExpertQuestions = (
    params: { query?: string } = {},
    signal?: AbortSignal
  ): Promise<AxiosResponse<GetExpertQuestionsResponse>> =>
    this.get(
      `ts/knowledge/expert/assigned-questions`,
      true,
      params,
      null,
      signal
    )

  updateExpertQuestion = (
    questionId: string,
    payload: {
      status: AnswerStatus
      answer?: string
      supportingDocuments?: File[]
    }
  ): Promise<AxiosResponse<DefaultSuccessResponse<ExpertQuestionDTO>>> => {
    const data = new FormData()

    data.append("status", payload.status)

    if (payload.answer) data.append("answer", payload.answer)

    payload.supportingDocuments?.forEach((file) =>
      data.append("supportingDocuments", file)
    )

    return this.patch(
      `ts/v2/knowledge/expert/assigned-questions/${questionId}`,
      data
    )
  }

  getExpertQuestionById = (
    questionId: string
  ): Promise<AxiosResponse<DefaultSuccessResponse<ExpertQuestionDTO>>> =>
    this.get(`ts/knowledge/expert/assigned-questions/${questionId}`)

  getExpertQuestionByElasticSearchId = (
    elasticSearchId: string
  ): Promise<AxiosResponse<DefaultSuccessResponse<ExpertQuestionDTO>>> =>
    this.get(
      `ts/knowledge/expert/assigned-questions-by-elastic-id/${elasticSearchId}`
    )

  removeExpertQuestionById = (
    questionId: string
  ): Promise<AxiosResponse<DefaultSuccessResponse>> =>
    this.delete(`ts/knowledge/expert/assigned-questions/${questionId}`)

  assignationQuestionDirectlyMultipleExperts = (request: {
    delegatedToUserIds: string[]
    question: string
    userNote?: string
    answer?: any
    summary?: string
    suppressNotification?: boolean
    avatarId?: string
    // LAB-47 routing context. With a companyId and no explicit experts the
    // backend resolves the brand's primary itself.
    companyId?: string
    questionType?: ExpertQuestionType
    questionId?: string
  }): Promise<AxiosResponse<DefaultSuccessResponse<ExpertQuestionDTO[]>>> =>
    this.post(`ts/v2/knowledge/expert/multi-assign`, request)

  // LAB-47: brand -> expert routing

  getRoutableBrands = (): Promise<
    AxiosResponse<DefaultSuccessResponse<RoutableBrand[]>>
  > => this.get(`ts/brand-experts/brands`)

  resolveBrandExpert = (
    companyId: string
  ): Promise<AxiosResponse<DefaultSuccessResponse<BrandExpertResolution>>> =>
    this.get(`ts/brand-experts/resolve`, true, { companyId })

  getBrandExperts = (
    companyId?: string
  ): Promise<AxiosResponse<DefaultSuccessResponse<BrandExpertMapping[]>>> =>
    this.get(`ts/brand-experts`, true, companyId ? { companyId } : undefined)

  createBrandExpert = (request: {
    companyId: string
    userId: string
    role?: BrandExpertRole
    priority?: number
  }): Promise<AxiosResponse<DefaultSuccessResponse<BrandExpertMapping>>> =>
    this.post(`ts/brand-experts`, request)

  updateBrandExpert = (
    id: string,
    request: { role?: BrandExpertRole; priority?: number; active?: boolean }
  ): Promise<AxiosResponse<DefaultSuccessResponse<BrandExpertMapping>>> =>
    this.patch(`ts/brand-experts/${id}`, request)

  deleteBrandExpert = (
    id: string
  ): Promise<AxiosResponse<DefaultSuccessResponse>> =>
    this.delete(`ts/brand-experts/${id}`)

  getExpertAnswersByAvatarId = (
    avatarId: string,
    params?: PaginationParams
  ): Promise<AxiosResponse<GetAvatarAnswersResponse>> =>
    this.get(`ts/v2/avatar/${avatarId}/expert-knowledge`, true, params)

  getDataTypesByAvatarId = (
    avatarId: string
  ): Promise<AxiosResponse<GetDataCategoriesByAvatarResponse>> =>
    this.get(`ts/v2/avatar/${avatarId}/knowledge-data-type`)

  getAllDataTypes = (
    query?: string,
    filters?: KnowledgeDatatypeFilters
  ): Promise<AxiosResponse<GetDataCategoriesByAvatarResponse>> =>
    this.get(`ts/knowledge-data-type/all`, true, { query, ...filters })

  getAllDataTypes$ = (): Observable<
    AxiosResponse<GetDataCategoriesByAvatarResponse>
  > => this.getStream$(`ts/knowledge-data-type/all`)

  updateDataTypes = (
    knowledgeDataTypeId: string,
    data: Partial<{
      description: string
      fileIds: string[]
      folderIds: string[]
      websiteIds: string[]
      fileIdsToDelete: string[]
      websiteIdsToDelete: string[]
      folderIdsToDelete: string[]
    }>
  ): Promise<AxiosResponse<GetDataCategoriesByAvatarResponse>> =>
    this.patch(`ts/knowledge-data-type`, data, true, {
      knowledgeDataTypeId,
    })

  // topics

  createExpertTopic = (
    avatarId: string,
    data: BasicTopic
  ): Promise<AxiosResponse<GetExpertTopicResponse>> =>
    this.post(`ts/expert-knowledge/${avatarId}/expert-topics`, data)

  getExpertTopicList = (
    avatarId: string,
    params?: PaginationParams & {
      deepSearch?: boolean
      mode?: CollectionContentModeType
    }
  ): Promise<AxiosResponse<GetExpertTopicListResponse>> =>
    this.get(`ts/expert-knowledge/${avatarId}/expert-topics`, true, params)

  updateExpertTopic = (
    avatarId: string,
    topicId: string,
    data: BasicTopic
  ): Promise<AxiosResponse<GetExpertTopicResponse>> =>
    this.patch(`ts/expert-knowledge/${avatarId}/expert-topics/${topicId}`, data)

  removeExpertTopic = (
    avatarId: string,
    topicId: string
  ): Promise<AxiosResponse<DefaultSuccessResponse>> =>
    this.delete(`ts/expert-knowledge/${avatarId}/expert-topics/${topicId}`)

  // subtopics

  createExpertSubtopic = (
    avatarId: string,
    topicId: string,
    data: BasicTopic
  ): Promise<AxiosResponse<GetExpertSubtopicResponse>> =>
    this.post(
      `ts/expert-knowledge/${avatarId}/expert-topics/${topicId}/expert-sub-topics`,
      data
    )

  getExpertSubtopicList = (
    avatarId: string,
    topicId: string,
    params?: PaginationParams & { mode?: CollectionContentModeType }
  ): Promise<AxiosResponse<GetExpertSubtopicListResponse>> =>
    this.get(
      `ts/expert-knowledge/${avatarId}/expert-topics/${topicId}/expert-sub-topics`,
      true,
      params
    )

  getExpertSubtopicById = (
    avatarId: string,
    subtopicId: string
  ): Promise<AxiosResponse<GetExpertSubtopicResponse>> =>
    this.get(`ts/expert-knowledge/${avatarId}/expert-sub-topics/${subtopicId}`)

  updateExpertSubtopic = (
    avatarId: string,
    subtopicId: string,
    data: BasicTopic
  ): Promise<AxiosResponse<GetExpertSubtopicResponse>> =>
    this.patch(
      `ts/expert-knowledge/${avatarId}/expert-sub-topics/${subtopicId}`,
      data
    )

  // articles

  getExpertArticleList = (
    avatarId: string,
    subtopicId: string,
    params?: PaginationParams & { mode?: CollectionContentModeType }
  ): Promise<AxiosResponse<GetExpertArticleListResponse>> =>
    this.get(
      `ts/expert-knowledge/${avatarId}/expert-sub-topics/${subtopicId}/expert-articles`,
      true,
      params
    )

  createExpertArticle = (
    avatarId: string,
    subtopicId: string,
    data: BasicTopic
  ): Promise<AxiosResponse<GetExpertArticleResponse>> =>
    this.post(
      `ts/expert-knowledge/${avatarId}/expert-sub-topics/${subtopicId}/expert-articles`,
      data
    )

  // insights

  createExpertArticleInsight = (
    avatarId: string,
    articleId: string,
    data: { insight: string }
  ): Promise<AxiosResponse<GetExpertArticleInsightResponse>> =>
    this.post(
      `ts/expert-knowledge/${avatarId}/expert-articles/${articleId}/expert-insights`,
      data
    )

  getExpertArticleInsightList = (
    avatarId: string,
    articleId: string,
    params?: PaginationParams
  ): Promise<AxiosResponse<GetExpertArticleInsightListResponse>> =>
    this.get(
      `ts/expert-knowledge/${avatarId}/expert-articles/${articleId}/expert-insights`,
      true,
      params
    )

  updateExpertArticleInsight = (
    avatarId: string,
    insightId: string,
    data: { insight: string }
  ): Promise<AxiosResponse<GetExpertArticleInsightResponse>> =>
    this.patch(
      `ts/expert-knowledge/${avatarId}/expert-insights/${insightId}`,
      data
    )

  deleteExpertArticleInsight = (
    avatarId: string,
    insightId: string
  ): Promise<AxiosResponse<DefaultSuccessResponse>> =>
    this.delete(`ts/expert-knowledge/${avatarId}/expert-insights/${insightId}`)

  sendReminderToExperts = (
    questionId: string,
    expertIds: string[]
  ): Promise<AxiosResponse<DefaultSuccessResponse>> =>
    this.post(
      `ts/v2/knowledge/expert/assigned-questions/${questionId}/send-reminder`,
      { expertIds },
      true
    )
}

export default new KnowledgeAPI()
